/* ==============================================
 * FileAttachmentPreview.jsx - Simplified file preview component
 * Shows attached files with basic preview and remove functionality
 * ============================================== */

import React, { useState, useCallback } from 'react';
import { X, FileText, Code, ClipboardIcon, Mic, Paperclip, Image as ImageIcon } from 'lucide-react';
import { useTheme } from '@/managers/ThemeProvider';

/* ========================
 * getFileIcon - Determines which icon to show based on file type
 * ========================*/
const getFileIcon = (file, isLightTheme) => {
    const iconColor = isLightTheme ? 'text-on-surface' : 'text-white';
    
    if (file.isPasted && !file.type.startsWith('image/')) {
        return <ClipboardIcon size={20} className={iconColor} />;
    }
    
    if (file.type.startsWith('image/')) {
        if (file.preview) {
            return <img src={file.preview} alt={file.name} className="w-10 h-10 object-cover rounded-md" />;
        }
        return <ImageIcon size={20} className={iconColor} />;
    }
    
    if (file.type.startsWith('audio/')) {
        return <Mic size={20} className={iconColor} />;
    }
    
    const ext = file.name?.split('.').pop()?.toLowerCase();
    const codeExtensions = ['js', 'jsx', 'ts', 'tsx', 'py', 'rb', 'java', 'cpp', 'c', 'cs', 'go', 'rs', 'php', 'html', 'css', 'json', 'yaml', 'yml', 'md', 'sql', 'sh', 'xml'];
    
    if (codeExtensions.includes(ext)) {
        return <Code size={20} className={iconColor} />;
    }
    
    if (file.type.startsWith('text/') || file.type === 'application/pdf') {
        return <FileText size={20} className={iconColor} />;
    }
    
    return <Paperclip size={20} className={iconColor} />;
};

/* ========================
 * ContentViewer - A modal for viewing file content
 * ========================*/
const ContentViewer = ({ file, onClose, onUpdate }) => {
    const { isLightTheme } = useTheme();
    const [editedContent, setEditedContent] = useState(file.content || '');
    
    const handleSave = useCallback(() => {
        if (onUpdate && editedContent !== file.content) {
            onUpdate(file.id, { content: editedContent });
        }
        onClose();
    }, [editedContent, file.content, file.id, onUpdate, onClose]);
    
    const isEditable = file.isEditable && file.isPasted && !file.type.startsWith('image/');
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className={`absolute inset-0 ${isLightTheme ? 'bg-black/40' : 'bg-black/75'} backdrop-blur-sm`} onClick={onClose} />
            <div className={`relative w-full max-w-2xl rounded-xl shadow-lg overflow-hidden ${isLightTheme ? 'bg-surface border border-border-color' : 'bg-surface-dark border border-border-color'}`}>
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isLightTheme ? 'bg-surface border-border-color/20' : 'bg-surface border-border-color'}`}>
                    <h3 className={`font-medium truncate ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>
                        {file.name || 'File Preview'}
                    </h3>
                    <div className="flex items-center gap-2">
                        {isEditable && (
                            <button
                                onClick={handleSave}
                                className={`px-3 py-1 text-sm rounded transition-colors ${isLightTheme ? 'bg-primary text-white hover:bg-primary-dark' : 'bg-primary text-white hover:bg-primary-dark'}`}
                            >
                                Save
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${isLightTheme ? 'hover:bg-surface-dark/10' : 'hover:bg-surface-hover'}`}
                        >
                            <X size={20} className={isLightTheme ? 'text-on-surface' : 'text-white'} />
                        </button>
                    </div>
                </div>

                <div className={`${isLightTheme ? 'bg-surface/50' : 'bg-surface-dark'} h-[400px] max-h-[70vh] overflow-y-auto`}>
                    {file.type.startsWith('image/') ? (
                        <div className="p-6 flex justify-center items-center h-full">
                            {file.preview ? (
                                <img src={file.preview} alt={file.name} className="max-w-full max-h-full object-contain rounded-lg" />
                            ) : (
                                <div className="text-center">
                                    <ImageIcon size={48} className="mx-auto mb-2 text-gray-400" />
                                    <p className="text-gray-500">Image preview not available</p>
                                </div>
                            )}
                        </div>
                    ) : isEditable ? (
                        <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            className={`w-full h-full p-4 resize-none border-0 focus:ring-0 text-sm ${isLightTheme ? 'bg-white text-black' : 'bg-surface-dark text-on-surface'}`}
                            placeholder="Edit content..."
                        />
                    ) : (
                        <div className="p-6">
                            <pre className={`text-sm whitespace-pre-wrap ${isLightTheme ? 'text-gray-900' : 'text-gray-100'}`}>
                                {file.content || 'No content available'}
                            </pre>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ========================
 * PreviewItem - Renders a single file attachment card
 * Handles special display logic for voice notes
 * ========================*/
const PreviewItem = ({ file, onRemove, onUpdate }) => {
    const [showViewer, setShowViewer] = useState(false);
    const { isLightTheme } = useTheme();

    // A file is a voice note if it's an audio file generated by our recording feature.
    const isVoiceNote = file.type.startsWith('audio/') && file.name.startsWith('voice_message_');

    const displayName = isVoiceNote ? 'Voice Note' :
                       file.isPasted && file.type.startsWith('image/') ? 'Pasted Image' :
                       file.isPasted ? (file.name || 'Pasted Content') :
                       file.name || 'Attached File';
    
    // A file has viewable content if it's NOT a voice note and has a preview.
    const hasViewableContent = !isVoiceNote && (file.content || file.preview);
    
    const itemClasses = `group relative rounded-xl p-3 transition-all duration-200 shadow-lg ${hasViewableContent ? 'cursor-pointer hover:scale-102' : 'cursor-default'} ${isLightTheme ? 'bg-surface border border-border-color' : 'bg-surface-dark border border-border-color'} ${hasViewableContent ? (isLightTheme ? 'hover:bg-surface-dark/10' : 'hover:bg-surface/90') : ''}`;

    return (
        <>
            <div
                onClick={hasViewableContent ? () => setShowViewer(true) : undefined}
                className={itemClasses}
            >
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-md ${isLightTheme ? 'bg-surface-dark/5' : 'bg-surface/10'}`}>
                        {getFileIcon(file, isLightTheme)}
                    </div>
                    <div className="flex flex-col min-w-0">
                        <div className={`text-sm font-medium truncate max-w-[150px] ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>
                            {displayName}
                        </div>
                        <div className={`text-xs ${isLightTheme ? 'text-on-surface/60' : 'text-white/60'}`}>
                            {file.status === 'transcribing' ? 'Transcribing...' :
                                (isVoiceNote ? 'Will transcribe when sent.' : // Moved voice note message here
                                (file.size ? `${(file.size / 1024).toFixed(1)} KB` :
                                (file.isPasted ? 'Pasted' : 'File')))
                            }
                        </div>
                    </div>
                    {onRemove && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onRemove(file.id); }}
                            className={`ml-2 p-1.5 opacity-0 group-hover:opacity-100 rounded-full transition-all ${isLightTheme ? 'hover:bg-surface-dark/10' : 'hover:bg-surface/20'}`}
                            aria-label="Remove file"
                        >
                            <X size={16} className={isLightTheme ? 'text-on-surface' : 'text-white'} />
                        </button>
                    )}
                </div>
            </div>

            {showViewer && hasViewableContent && (
                <ContentViewer
                    file={file}
                    onClose={() => setShowViewer(false)}
                    onUpdate={onUpdate}
                />
            )}
        </>
    );
};

/* ========================
 * FileAttachmentPreview - The main container for all file previews
 * ========================*/
const FileAttachmentPreview = ({ files = {}, onRemoveFile, onUpdateFile }) => {
    const fileArray = Object.values(files);
    if (!fileArray.length) return null;

    return (
        <div className="absolute bottom-full left-0 right-0 mb-1 z-30">
            <div className="relative w-full px-2 mx-auto max-w-3xl">
                <div className="flex flex-row flex-wrap items-start justify-start gap-2 pb-2">
                    {fileArray.map((file) => (
                        <PreviewItem
                            key={file.id}
                            file={file}
                            onRemove={onRemoveFile}
                            onUpdate={onUpdateFile}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FileAttachmentPreview;